import { FunctionComponent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { updateProfileField } from "./store/userSlice";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getDB } from "./Firebase";

const DAILY_KEY = "bg_daily_v1";
const DAY_MS = 24 * 60 * 60 * 1000;

interface DailyState {
  lastClaim: number;
  streak: number;
}

// Суточные бонусы: 1–5 ₽, 7-й день максимум
const STREAK_REWARDS = [1, 1, 2, 2, 3, 4, 5];

const TASKS = [
  { id: "play3",   label: "Сыграть 3 игры",      reward: 1,  icon: "◈" },
  { id: "win2",    label: "Победить 2 раза",       reward: 2,  icon: "◆" },
  { id: "gammon1", label: "Выиграть гаммон",        reward: 3,  icon: "★" },
];

type DailyBonusProps = {
  onClose: () => void;
};

const DailyBonus: FunctionComponent<DailyBonusProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.user.profile);

  const [daily, setDaily] = useState<DailyState>({ lastClaim: 0, streak: 0 });
  const [claimed, setClaimed] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");

  const now = Date.now();
  const canClaim = now - daily.lastClaim >= DAY_MS;
  const streakIdx = Math.min(daily.streak, STREAK_REWARDS.length - 1);
  const reward = STREAK_REWARDS[streakIdx];

  useEffect(() => {
    const raw = localStorage.getItem(DAILY_KEY);
    if (raw) setDaily(JSON.parse(raw));
  }, []);

  useEffect(() => {
    if (canClaim) return;
    const interval = setInterval(() => {
      const left = DAY_MS - (Date.now() - daily.lastClaim);
      if (left <= 0) { setTimeLeft(""); return; }
      const h = Math.floor(left / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      setTimeLeft(`${h}ч ${m}м`);
    }, 30000);
    const left = DAY_MS - (Date.now() - daily.lastClaim);
    const h = Math.floor(left / 3600000);
    const m = Math.floor((left % 3600000) / 60000);
    setTimeLeft(`${h}ч ${m}м`);
    return () => clearInterval(interval);
  }, [daily.lastClaim, canClaim]);

  const handleClaim = async () => {
    if (!canClaim || claimed || !profile) return;

    const newStreak = now - daily.lastClaim < DAY_MS * 2 ? daily.streak + 1 : 1;
    const newState: DailyState = { lastClaim: now, streak: newStreak };
    localStorage.setItem(DAILY_KEY, JSON.stringify(newState));
    setDaily(newState);
    setClaimed(true);

    dispatch(updateProfileField({ coins: (profile.coins || 0) + reward }));

    // Синхронизируем с Firestore
    try {
      const db = getDB();
      const ref = doc(db, "users", profile.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const cur = snap.data().coins || 0;
        await setDoc(ref, { coins: cur + reward, lastDailyClaim: serverTimestamp() }, { merge: true });
      }
    } catch (_) {}
  };

  return (
    <div className="Daily-overlay" onClick={onClose}>
      <div className="Daily-modal" onClick={(e) => e.stopPropagation()}>
        <button className="Daily-close" onClick={onClose}>&#x2715;</button>

        <h2 className="Daily-title">Ежедневный бонус</h2>

        {/* Серия */}
        <div className="Daily-streak-row">
          {STREAK_REWARDS.map((r, i) => (
            <div
              key={i}
              className={`Daily-streak-day ${i < daily.streak ? "done" : ""} ${i === daily.streak ? "current" : ""}`}
            >
              <div className="Daily-streak-num">День {i + 1}</div>
              <div className="Daily-streak-reward">{r} ₽</div>
            </div>
          ))}
        </div>

        {/* Кнопка получения */}
        <div className="Daily-claim-section">
          {canClaim && !claimed ? (
            <>
              <div className="Daily-reward-amount">+{reward} ₽</div>
              <button className="Daily-claim-btn" onClick={handleClaim}>
                Получить бонус
              </button>
            </>
          ) : claimed ? (
            <div className="Daily-claimed-msg">Бонус получен! Возвращайтесь завтра.</div>
          ) : (
            <div className="Daily-timer">
              Следующий бонус через <strong>{timeLeft}</strong>
            </div>
          )}
        </div>

        {/* Задания */}
        <div className="Daily-tasks-section">
          <h3 className="Daily-tasks-title">Ежедневные задания</h3>
          <div className="Daily-tasks-list">
            {TASKS.map((t) => (
              <div key={t.id} className="Daily-task">
                <div className="Daily-task-icon">{t.icon}</div>
                <div className="Daily-task-label">{t.label}</div>
                <div className="Daily-task-reward">+{t.reward} ₽</div>
                <div className="Daily-task-status">Скоро</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyBonus;
